/**
 * web/main.ts
 *
 * Vite entry point. Wires the SPA router to the six top-level routes,
 * injects shared styles, and kicks off the asset load in the background.
 *
 * Exported {@link bootApp} so tests can drive boot under jsdom without
 * the auto-run DOMContentLoaded hook.
 *
 * @module web/main
 */

/// <reference lib="dom" />

import { createRouter } from './router';
import { injectStyles } from './styles';
import { loadUserSettings } from '@src/game/services/userSettings';
import { loadGameAssets } from './assets';
import { registerWebAudioSources } from './audioAssets';
import { installAudioUnlock } from '@src/game/services/audioManager';
import { menuScreen } from './screens/menu';
import { settingsScreen } from './screens/settings';
import { characterSelectScreen } from './screens/characterSelect';
import { worldMapScreen } from './screens/worldMap';
import { gameScreen } from './screens/game';
import { spaceGameScreen } from './screens/spaceGame';

interface BootHandle {
  /** Tear down the router (clears the current screen). Tests + HMR. */
  stop: () => void;
}

/**
 * Boot the app into `root`. Idempotent guard lives in the router itself.
 *
 * @param root Container element. In production this is `<div id="app">`.
 */
export async function bootApp(root: HTMLElement): Promise<BootHandle> {
  injectStyles();
  await loadUserSettings();
  // Fire-and-forget the asset load — screens render their layout immediately
  // and the canvas paints greybox until images resolve.
  void loadGameAssets();

  // Audio URLs are registered eagerly (cheap — just strings), but the
  // AudioContext is not built until the player's first gesture: browsers
  // refuse to start one before then, and a suspended context silences every
  // element routed through it.
  registerWebAudioSources();
  const stopAudioUnlock = installAudioUnlock();

  const router = createRouter(root, {
    '/': menuScreen,
    '/menu': menuScreen,
    '/character-select': characterSelectScreen,
    '/world-map': worldMapScreen,
    '/settings': settingsScreen,
    '/game': gameScreen,
    '/space-game': spaceGameScreen,
  }, '/');

  return {
    stop: () => {
      stopAudioUnlock();
      router.destroy();
    },
  };
}

if (typeof document !== 'undefined') {
  const boot = (): void => {
    const root = document.getElementById('app');
    if (root) void bootApp(root);
  };
  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', boot, { once: true });
  } else {
    boot();
  }
}
